// XSL stylesheet for rendering a CDM/OCF/PP HL7 ORU_R01 clinical message as a
// readable, printable HTML view. The HL7 message is self-describing: each
// ORU_R01.ORDER_OBSERVATION carries a section title in OBR.4/CE.2, and each OBX
// holds a field label (OBX.3/CE.2) with its value (OBX.5 — plain text or a coded
// CE.2) and optional unit (OBX.6/CE.2). A single generic stylesheet therefore
// covers all three programmes. Fed through HealthlinkXsltFrame (XSLTProcessor +
// sandboxed iframe), so the styling lives inline in the transform output.
export const CDM_MESSAGE_XSL = `<?xml version="1.0" encoding="UTF-8"?>
<xsl:stylesheet version="1.0"
  xmlns:xsl="http://www.w3.org/1999/XSL/Transform"
  xmlns:hl7="urn:hl7-org:v2xml">
  <xsl:output method="html" omit-xml-declaration="yes" indent="yes"/>

  <xsl:template name="formatDate">
    <xsl:param name="value"/>
    <xsl:choose>
      <xsl:when test="string-length($value) &gt;= 8">
        <xsl:value-of select="substring($value, 7, 2)"/>-<xsl:value-of select="substring($value, 5, 2)"/>-<xsl:value-of select="substring($value, 1, 4)"/>
      </xsl:when>
      <xsl:otherwise><xsl:value-of select="$value"/></xsl:otherwise>
    </xsl:choose>
  </xsl:template>

  <xsl:template match="/">
    <div class="cdm-msg">
      <style>
        .cdm-msg { color: #1f2937; font-size: 14px; line-height: 1.5; }
        .cdm-msg h1 { text-align: center; font-size: 18px; font-weight: 700; color: #111827; margin: 8px 0 20px; }
        .cdm-msg hr { color: gray; border-style: inset; border-width: 1px; margin: 8px 0; }
        .cdm-msg .patient { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 32px; padding: 8px 0; }
        .cdm-msg .patient .row { display: flex; gap: 8px; min-width: 0; }
        .cdm-msg .patient .k { font-weight: 700; color: #111827; flex: 0 0 140px; }
        .cdm-msg .patient .row span:last-child { min-width: 0; word-break: break-word; }
        .cdm-msg .section { margin-bottom: 32px; }
        .cdm-msg .section h2 { font-size: 15px; font-weight: 700; color: #111827; margin: 28px 0 4px; }
        .cdm-msg table { width: 100%; border-collapse: collapse; }
        .cdm-msg td { padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #e5e7eb; }
        .cdm-msg td.label { font-weight: 700; color: #111827; width: 45%; }
        .cdm-msg td.value { color: #1f2937; }
      </style>
      <xsl:apply-templates select="hl7:ORU_R01"/>
    </div>
  </xsl:template>

  <xsl:template match="hl7:ORU_R01">
    <h1>CDM Clinical Message</h1>
    <hr/>
    <xsl:apply-templates select=".//hl7:ORU_R01.PATIENT"/>
    <!-- KIN-1347: skip the programme-header OBR (Vendor Version ID, Consultation
         Type, Message Version No, control ids). It is identified by its OBR.4
         code: X0135-0 (CDM), X0329-0 (PP), X0330-0 (OCF). Dropping it here keeps
         the review-detail view consistent with the clinical export (KIN-1346);
         the audit clinicalMessageXml itself is left untouched. -->
    <xsl:apply-templates select=".//hl7:ORU_R01.ORDER_OBSERVATION[
      not(hl7:OBR/hl7:OBR.4/hl7:CE.1 = 'X0135-0'
        or hl7:OBR/hl7:OBR.4/hl7:CE.1 = 'X0329-0'
        or hl7:OBR/hl7:OBR.4/hl7:CE.1 = 'X0330-0')]"/>
  </xsl:template>

  <xsl:template match="hl7:ORU_R01.PATIENT">
    <xsl:variable name="pid" select="hl7:PID"/>
    <xsl:variable name="pv1" select="hl7:ORU_R01.PATIENT_VISIT/hl7:PV1"/>
    <div class="patient">
      <div class="row">
        <span class="k">Patient</span>
        <span><xsl:value-of select="$pid/hl7:PID.5/hl7:XPN.2"/><xsl:text> </xsl:text><xsl:value-of select="$pid/hl7:PID.5/hl7:XPN.1/hl7:FN.1"/></span>
      </div>
      <div class="row">
        <span class="k">Date of birth</span>
        <span>
          <xsl:call-template name="formatDate">
            <xsl:with-param name="value" select="$pid/hl7:PID.7/hl7:TS.1"/>
          </xsl:call-template>
        </span>
      </div>
      <div class="row">
        <span class="k">Sex</span>
        <span><xsl:value-of select="$pid/hl7:PID.8"/></span>
      </div>
      <div class="row">
        <span class="k">Patient number</span>
        <span><xsl:value-of select="$pid/hl7:PID.3/hl7:CX.1"/><xsl:if test="$pid/hl7:PID.3/hl7:CX.5"><xsl:text> (</xsl:text><xsl:value-of select="$pid/hl7:PID.3/hl7:CX.5"/><xsl:text>)</xsl:text></xsl:if></span>
      </div>
      <xsl:if test="$pid/hl7:PID.22/hl7:CE.2">
        <div class="row">
          <span class="k">Ethnicity</span>
          <span><xsl:value-of select="$pid/hl7:PID.22/hl7:CE.2"/></span>
        </div>
      </xsl:if>
      <xsl:if test="$pv1/hl7:PV1.7">
        <div class="row">
          <span class="k">Reviewing doctor</span>
          <span><xsl:value-of select="$pv1/hl7:PV1.7/hl7:XCN.3"/><xsl:text> </xsl:text><xsl:value-of select="$pv1/hl7:PV1.7/hl7:XCN.2/hl7:FN.1"/></span>
        </div>
      </xsl:if>
    </div>
    <hr/>
  </xsl:template>

  <xsl:template match="hl7:ORU_R01.ORDER_OBSERVATION">
    <div class="section">
      <h2><xsl:value-of select="hl7:OBR/hl7:OBR.4/hl7:CE.2"/></h2>
      <hr/>
      <table>
        <xsl:apply-templates select="hl7:ORU_R01.OBSERVATION/hl7:OBX"/>
      </table>
    </div>
  </xsl:template>

  <xsl:template match="hl7:OBX">
    <tr>
      <td class="label"><xsl:value-of select="hl7:OBX.3/hl7:CE.2"/></td>
      <td class="value">
        <xsl:choose>
          <xsl:when test="hl7:OBX.5/hl7:CE.2">
            <xsl:value-of select="hl7:OBX.5/hl7:CE.2"/>
          </xsl:when>
          <xsl:otherwise>
            <xsl:value-of select="hl7:OBX.5"/>
          </xsl:otherwise>
        </xsl:choose>
        <xsl:if test="hl7:OBX.6/hl7:CE.2">
          <xsl:text> </xsl:text><xsl:value-of select="hl7:OBX.6/hl7:CE.2"/>
        </xsl:if>
      </td>
    </tr>
  </xsl:template>
</xsl:stylesheet>`;
